import { CaretRight } from '@liveauctioneers/hammer-ui-core/icons';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { TextInput } from '@liveauctioneers/hammer-ui-core/textInput';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { difference, union } from 'es-toolkit';
import { ChangeEvent, KeyboardEvent, useState } from 'react';

import { Folder } from './components/Folder';
import { Folder as FolderType } from './types/Folder';
import { InboxFolderStats } from './types/InboxFolderStats';

type Props = {
    readonly activeFolder: number | null;
    readonly className?: string;
    readonly formatNumber: Function;
    readonly hideUserFolders?: boolean;
    readonly inboxFolders: InboxFolderStats;
    readonly loading: boolean;
    readonly onArchiveFolder: Function;
    readonly onCreateFolder: Function;
    readonly onRenameFolder: Function;
    readonly onSetActiveInbox: Function;
    readonly testid?: string;
};

export const Folders = ({
    activeFolder = 0,
    className,
    formatNumber = (num: number) => num,
    hideUserFolders,
    inboxFolders,
    loading = false,
    onArchiveFolder,
    onCreateFolder,
    onRenameFolder = (name: any) => name,
    onSetActiveInbox,
    testid = 'messengerFolders',
}: Props) => {
    const { systemFolders = [], userFolders = [] } = inboxFolders;
    const [edit, setEdit] = useState(false);
    const [expand, setExpand] = useState(false);
    const [name, setName] = useState('');
    const [archiveing, setArchiving] = useState<any>([]);
    const [renaming, setRenaming] = useState<any>([]);

    const handleKeyPress = (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
            onCreateFolder(name.trim());
            setName('');
        }
        event.stopPropagation();
    };

    const handleButtonClick = () => {
        if (name.trim()) {
            onCreateFolder(name.trim());
            setName('');
        }
    };

    const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
        const target = event.target;
        setName(target.value);
        event.stopPropagation();
    };

    const handleCancel = () => {
        setEdit(false);
        setExpand(false);
        setArchiving([]);
        setRenaming([]);
    };

    const inboxFolder: FolderType = systemFolders.find((f) => f.id === 0) as FolderType;
    const sentFolder: FolderType = systemFolders.find((f) => f.id === 1) as FolderType;
    const archiveFolder: FolderType = systemFolders.find((f) => f.id === -1) as FolderType;

    return (
        <div className={className}>
            <ul
                className="mb-xs p-0"
                data-testid={testid}
            >
                {Boolean(inboxFolder?.name) && (
                    <Folder
                        activeFolder={activeFolder}
                        count={inboxFolder.unReadConversationCount && formatNumber(inboxFolder.unReadConversationCount)}
                        id={inboxFolder.id}
                        key={inboxFolder.id}
                        loading={loading}
                        name="Inbox"
                        onSetActiveInbox={onSetActiveInbox}
                    />
                )}
                {Boolean(sentFolder?.name) && (
                    <Folder
                        activeFolder={activeFolder}
                        count={sentFolder.unReadConversationCount && formatNumber(sentFolder.unReadConversationCount)}
                        id={sentFolder.id}
                        key={sentFolder.id}
                        loading={loading}
                        name="Sent"
                        onSetActiveInbox={onSetActiveInbox}
                    />
                )}
                {Boolean(archiveFolder?.name) && (
                    <Folder
                        activeFolder={activeFolder}
                        count={
                            archiveFolder.unReadConversationCount && formatNumber(archiveFolder.unReadConversationCount)
                        }
                        id={archiveFolder.id}
                        key={archiveFolder.id}
                        loading={loading}
                        name="Archive"
                        onSetActiveInbox={onSetActiveInbox}
                    />
                )}
            </ul>
            {!hideUserFolders && (
                <div className="border-t border-border-secondary pt-[15px]">
                    <div className="mb-[6px] flex flex-row items-center justify-between px-xs">
                        <BodySecondary
                            className="uppercase"
                            color="secondary"
                        >
                            Folders
                        </BodySecondary>
                        {!edit ? (
                            <InlineButton
                                onClick={() => setEdit(!edit)}
                                size="sm"
                            >
                                Edit
                            </InlineButton>
                        ) : (
                            <div>
                                <InlineButton
                                    onClick={() => setExpand(!expand)}
                                    size="sm"
                                >
                                    New
                                </InlineButton>
                                <span className="text-text-inactive"> | </span>
                                <InlineButton
                                    onClick={handleCancel}
                                    size="sm"
                                >
                                    Cancel
                                </InlineButton>
                            </div>
                        )}
                    </div>
                    <div
                        className={cn(
                            'flex w-[204px] flex-row items-center overflow-hidden px-xs [transition:height_0.25s]',
                            expand ? 'h-[56px] max-h-[56px]' : 'h-0 max-h-0'
                        )}
                    >
                        <TextInput
                            aria-label="New folder name"
                            id="newFolder"
                            onChange={handleOnChange}
                            onKeyUp={handleKeyPress}
                            placeholder="New folder name"
                            value={name}
                        />
                        <div
                            className="[&_svg]:text-white flex h-[34px] w-[30px] cursor-pointer items-center justify-center bg-primary-500 hover:bg-primary-400"
                            onClick={handleButtonClick}
                        >
                            <CaretRight size="xs" />
                        </div>
                    </div>
                    <ul className="mb-[20px] h-auto max-h-[1200px] overflow-auto p-0">
                        {userFolders.map(({ id, name, unReadConversationCount }) => (
                            <Folder
                                activeFolder={activeFolder}
                                archive={archiveing.includes(id)}
                                count={unReadConversationCount && formatNumber(unReadConversationCount)}
                                edit={edit}
                                id={id}
                                key={id}
                                loading={loading}
                                name={name}
                                onArchiveFolder={onArchiveFolder}
                                onRenameFolder={onRenameFolder}
                                onSetActiveInbox={onSetActiveInbox}
                                renaming={renaming.includes(id)}
                                setArchive={(archive: boolean) =>
                                    archive
                                        ? setArchiving(union(archiveing, [id]))
                                        : setArchiving(difference(archiveing, [id]))
                                }
                                setRenaming={(rename: boolean) =>
                                    rename
                                        ? setRenaming(union(renaming, [id]))
                                        : setRenaming(difference(renaming, [id]))
                                }
                            />
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};
